/**
 * Pieces of the Creators dashboard's Brand logos tab: the logo plate and the
 * initials badge that stands in for a missing logo, the drop targets that take
 * a PNG or JPG, the suggestion card, and the modals — Find (search for a free
 * logo or upload one), Same as… (link a report name to a registry advertiser
 * as one of its aliases) and Add a brand (one on no report and not in the
 * registry yet).
 *
 * Plates use the creator report page's colors (--logo-plate-light/-dark), so a
 * logo looks here the way clients will see it. Every preview image is loaded
 * with referrerPolicy="no-referrer".
 */

import React, { useId, useMemo, useState } from 'react';
import { Button, Modal, Spinner, StatusPill } from '../ui';
import type { AdvertiserRecord } from '../Sales/MediaPlans/types';
import { fold } from './creatorDashboardSearch';
import { fieldStyle, labelStyle } from './formParts';
import { initialsOf, provenanceOf, suggestedRecordOf } from './brandLogoModel';
import type { AdvertiserLogo, AdvertiserLogoRow, LogoBackground, LogoCandidate } from './types';

const mutedStyle: React.CSSProperties = { fontSize: 12, color: 'var(--fg-4)' };

/** Only a drag that carries files can drop a logo (not text or a link from the page). */
export const isFileDrag = (e: { dataTransfer?: { types?: ArrayLike<string> | null } | null }): boolean =>
  Array.from(e.dataTransfer?.types ?? []).includes('Files');

/** Drag handlers that take dropped files; `over` drives the highlight. */
function fileDropHandlers(setOver: (over: boolean) => void, onFiles: (files: File[]) => void) {
  return {
    onDragOver: (e: React.DragEvent) => {
      if (!isFileDrag(e)) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
      setOver(true);
    },
    onDragLeave: () => setOver(false),
    onDrop: (e: React.DragEvent) => {
      if (!isFileDrag(e)) return;
      e.preventDefault();
      setOver(false);
      onFiles(Array.from(e.dataTransfer.files ?? []));
    },
  };
}

/** A row's plate (or initials) as a drop target: drop a PNG or JPG on it to upload. */
export const LogoDropTarget: React.FC<{
  name: string;
  disabled: boolean;
  onFiles: (files: File[]) => void;
  children: React.ReactNode;
}> = ({ name, disabled, onFiles, children }) => {
  const [over, setOver] = useState(false);
  return (
    <div
      data-drop-target=""
      title={disabled ? undefined : `Drop a PNG or JPG here to upload ${name}’s logo`}
      {...(disabled ? {} : fileDropHandlers(setOver, onFiles))}
      style={{
        position: 'relative',
        display: 'inline-flex',
        borderRadius: 'var(--radius-sm)',
        outline: over ? '2px dashed var(--accent)' : 'none',
        outlineOffset: 3,
      }}
    >
      {children}
      {over && (
        <span
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 'var(--radius-sm)',
            background: 'var(--accent-soft)',
            color: 'var(--accent-soft-fg)',
            fontSize: 11.5,
            fontWeight: 700,
            textAlign: 'center',
            pointerEvents: 'none',
          }}
        >
          Drop to upload
        </span>
      )}
    </div>
  );
};

/** The Find modal's upload path: drop a PNG or JPG on the strip, or choose one. */
const UploadDropZone: React.FC<{
  uploading: boolean;
  disabled: boolean;
  onChoose: () => void;
  onFiles: (files: File[]) => void;
}> = ({ uploading, disabled, onChoose, onFiles }) => {
  const [over, setOver] = useState(false);
  return (
    <div
      data-drop-target=""
      {...(disabled ? {} : fileDropHandlers(setOver, onFiles))}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        flexWrap: 'wrap',
        padding: '12px 14px',
        borderRadius: 'var(--radius-lg)',
        border: `1.5px dashed ${over ? 'var(--accent)' : 'var(--border-1)'}`,
        background: over ? 'var(--accent-soft)' : 'var(--bg-surface-2)',
        fontSize: 13,
      }}
    >
      <div style={{ minWidth: 0, flex: '1 1 260px' }}>
        <div style={{ fontWeight: 600 }}>{uploading ? 'Uploading…' : over ? 'Drop to upload' : 'Have the logo file? Drop a PNG or JPG here.'}</div>
        <div style={{ ...mutedStyle, marginTop: 2 }}>Up to 5 MB and at least 64 px across. It’s trimmed and resized for you.</div>
      </div>
      <Button size="sm" variant="ghost" onClick={onChoose} disabled={disabled}>
        Choose a file…
      </Button>
    </div>
  );
};

const bannerStyle = (tone: 'info' | 'danger'): React.CSSProperties => ({
  padding: '10px 14px',
  borderRadius: 'var(--radius-md)',
  background: `var(--${tone}-soft)`,
  fontSize: 13,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 10,
  flexWrap: 'wrap',
});

/** Initials on a tinted disc — what report pages show for a brand without a logo. */
export const InitialsBadge: React.FC<{ name: string; size?: number }> = ({ name, size = 40 }) => (
  <span
    role="img"
    aria-label={`No logo for ${name}`}
    style={{
      width: size,
      height: size,
      borderRadius: '50%',
      background: 'var(--accent-soft)',
      color: 'var(--accent-soft-fg)',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: Math.round(size * 0.36),
      fontWeight: 700,
      letterSpacing: '0.02em',
      fontFamily: 'var(--font-sans)',
      flexShrink: 0,
    }}
  >
    {initialsOf(name)}
  </span>
);

/** A logo on its plate. A preview that fails to load says so instead of showing a broken image. */
export const LogoPlate: React.FC<{
  src: string;
  background: LogoBackground;
  alt: string;
  width: number | string;
  height: number;
}> = ({ src, background, alt, width, height }) => {
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const dark = background === 'dark';
  return (
    <div
      data-plate={background}
      style={{
        width,
        height,
        boxSizing: 'border-box',
        padding: 6,
        borderRadius: 'var(--radius-sm)',
        border: '1px solid var(--border-1)',
        backgroundColor: dark ? 'var(--logo-plate-dark)' : 'var(--logo-plate-light)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        flexShrink: 0,
      }}
    >
      {failedSrc === src ? (
        <span style={{ fontSize: 10.5, color: dark ? 'var(--cg-neutral-200)' : 'var(--fg-4)' }}>Preview unavailable</span>
      ) : (
        <img
          src={src}
          alt={alt}
          referrerPolicy="no-referrer"
          loading="lazy"
          decoding="async"
          onError={() => setFailedSrc(src)}
          style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', display: 'block' }}
        />
      )}
    </div>
  );
};

const dims = (c: { width?: number; height?: number }) => (c.width && c.height ? `${c.width}×${c.height}` : null);

/** One suggestion: the image on a white and a dark plate, where it's from, and "Use this logo". */
export const CandidateCard: React.FC<{
  candidate: LogoCandidate;
  suggested: boolean;
  saving: boolean;
  disabled: boolean;
  onUse: () => void;
}> = ({ candidate, suggested, saving, disabled, onUse }) => {
  const meta = [provenanceOf(candidate.source, candidate.license), dims(candidate)].filter(Boolean).join(' · ');
  return (
    <div
      role="group"
      aria-label={candidate.label}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        padding: 12,
        borderRadius: 'var(--radius-lg)',
        border: `1px solid ${suggested ? 'var(--accent-2)' : 'var(--border-1)'}`,
        background: 'var(--bg-surface-1)',
        minWidth: 0,
      }}
    >
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
        <LogoPlate src={candidate.previewUrl} background="light" alt="" width="100%" height={64} />
        <LogoPlate src={candidate.previewUrl} background="dark" alt="" width="100%" height={64} />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        <strong style={{ fontSize: 13, overflowWrap: 'anywhere' }}>{candidate.label}</strong>
        {suggested && <StatusPill variant="featured" size="sm" noDot>Top pick</StatusPill>}
      </div>
      {candidate.description && (
        <div
          style={{
            ...mutedStyle,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
          title={candidate.description}
        >
          {candidate.description}
        </div>
      )}
      <div style={{ ...mutedStyle, fontSize: 11.5 }}>
        {meta}
        {candidate.sourceUrl && (
          <>
            {' · '}
            <a href={candidate.sourceUrl} target="_blank" rel="noreferrer" style={{ color: 'var(--fg-link)' }}>
              Source page ↗
            </a>
          </>
        )}
      </div>
      <div style={{ marginTop: 'auto' }}>
        <Button size="sm" variant="primary" fullWidth onClick={onUse} disabled={disabled} aria-label={`Use this logo: ${candidate.label}`}>
          {saving ? 'Saving…' : 'Use this logo'}
        </Button>
      </div>
    </div>
  );
};

export interface FoundLogos {
  candidates: LogoCandidate[];
  error?: string;
  /** The name the finder was asked for (the row's own unless someone typed another). */
  searchedFor?: string;
}

/**
 * Find: upload the logo file, or pick a free logo the finder suggests for one
 * registry advertiser. "Use this logo" saves straight away. "Search for" asks
 * the finder for another name — the full company name finds what a short one
 * can't ("Mars Incorporated", not the planet). The logo belongs to the
 * registry record, so the modal names every report name it will show for.
 */
export const FindLogoModal: React.FC<{
  rowName: string;
  recordName: string;
  /** Every report name linked to the record, this one included. */
  linkedNames: string[];
  currentLogo: AdvertiserLogo | null;
  found: FoundLogos | undefined;
  loading: boolean;
  /** The suggestion staged for this record, marked "Top pick". */
  stagedId?: string;
  savingId: string | null;
  uploading: boolean;
  onRetry: () => void;
  onSearch: (name: string) => void;
  onUse: (candidate: LogoCandidate) => void;
  onUpload: () => void;
  onFiles: (files: File[]) => void;
  onClose: () => void;
}> = ({
  rowName,
  recordName,
  linkedNames,
  currentLogo,
  found,
  loading,
  stagedId,
  savingId,
  uploading,
  onRetry,
  onSearch,
  onUse,
  onUpload,
  onFiles,
  onClose,
}) => {
  const id = useId();
  const others = linkedNames.filter((n) => n !== rowName);
  const busy = savingId !== null || uploading;
  const [term, setTerm] = useState(found?.searchedFor ?? rowName);
  const searchedFor = found?.searchedFor ?? rowName;
  return (
    <Modal
      open
      onClose={onClose}
      eyebrow="Brand logos"
      title={`Logos for ${rowName}`}
      ariaLabel={`Logos for ${rowName}`}
      width={780}
      footer={
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <Button variant="ghost" onClick={onClose}>Done</Button>
        </div>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ fontSize: 13, color: 'var(--fg-3)' }}>
          Upload the logo file, or pick a free one: public-domain and CC0 files from Wikimedia Commons, or the wordmarks on
          culturegenesis.com. Each is shown on a white and a dark plate. The saved logo is trimmed and resized, and you can switch
          its plate afterwards.
        </div>

        {(recordName !== rowName || others.length > 0) && (
          <div style={{ fontSize: 12.5, color: 'var(--fg-3)' }}>
            Saved on the registry advertiser <strong>{recordName}</strong>
            {others.length > 0 && <>, so it also shows for {others.map((n) => `“${n}”`).join(', ')}</>}.
          </div>
        )}

        {currentLogo && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={labelStyle}>Current logo</span>
            <LogoPlate src={currentLogo.url} background={currentLogo.background} alt={`Current ${recordName} logo`} width={120} height={48} />
          </div>
        )}

        <UploadDropZone uploading={uploading} disabled={busy} onChoose={onUpload} onFiles={onFiles} />

        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (term.trim() && !loading) onSearch(term.trim());
          }}
          style={{ display: 'flex', alignItems: 'flex-end', gap: 8, flexWrap: 'wrap' }}
        >
          <div style={{ flex: '1 1 240px', minWidth: 0 }}>
            <label htmlFor={`${id}-term`} style={labelStyle}>Search for</label>
            <input id={`${id}-term`} style={fieldStyle} value={term} maxLength={200} onChange={(e) => setTerm(e.target.value)} />
          </div>
          <Button type="submit" variant="subtle" disabled={loading || !term.trim()}>
            Search
          </Button>
          <div style={{ ...mutedStyle, flexBasis: '100%' }}>
            Found the wrong brand, or nothing? Try the company’s full name — “Mars Incorporated” rather than “MARS”.
          </div>
        </form>

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}>
            <Spinner label="Searching Wikimedia Commons and culturegenesis.com…" />
          </div>
        ) : found?.error ? (
          <div role="alert" style={bannerStyle('danger')}>
            <span>
              <strong>The search failed.</strong> <span style={{ color: 'var(--fg-3)' }}>{found.error}</span>
            </span>
            <Button size="sm" variant="ghost" onClick={onRetry}>Try again</Button>
          </div>
        ) : found && found.candidates.length === 0 ? (
          <div
            style={{
              padding: '24px 16px',
              textAlign: 'center',
              border: '1px dashed var(--border-1)',
              borderRadius: 'var(--radius-lg)',
              color: 'var(--fg-3)',
              fontSize: 13,
            }}
          >
            No free logo found for “{searchedFor}”. Search for another name, or upload the logo file.
          </div>
        ) : found ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
            {found.candidates.map((c) => (
              <CandidateCard
                key={c.id}
                candidate={c}
                suggested={c.id === stagedId}
                saving={savingId === c.id}
                disabled={busy}
                onUse={() => onUse(c)}
              />
            ))}
          </div>
        ) : null}
      </div>
    </Modal>
  );
};

/**
 * Same as…: link a report name to an existing registry advertiser by adding
 * the exact name as one of its aliases. Starts on the loose match or the GAM
 * id hint when the row has one; an ambiguous row lists its matches first.
 */
export const SameAsDialog: React.FC<{
  row: AdvertiserLogoRow;
  /** Pre-select this record instead of the row's own suggestion. */
  initialKey?: string;
  advertisers: AdvertiserRecord[] | null;
  loading: boolean;
  loadError: string | null;
  onRetryLoad: () => void;
  /** Logos of the records the list already knows, to preview what will show. */
  logoByKey: ReadonlyMap<string, AdvertiserLogo>;
  saving: boolean;
  error: string | null;
  onLink: (record: AdvertiserRecord) => void;
  onClose: () => void;
}> = ({ row, initialKey, advertisers, loading, loadError, onRetryLoad, logoByKey, saving, error, onLink, onClose }) => {
  const id = useId();
  const suggestion = useMemo(() => suggestedRecordOf(row), [row]);
  const [filter, setFilter] = useState('');
  const [pickedKey, setPickedKey] = useState(initialKey ?? suggestion?.key ?? '');

  const topKeys = useMemo(() => {
    if (row.match.state === 'ambiguous') return row.match.candidates.map((c) => c.key);
    return suggestion ? [suggestion.key] : [];
  }, [row, suggestion]);
  const topLabel = row.match.state === 'ambiguous' ? 'Matches this name' : 'Suggested';

  const { top, rest } = useMemo(() => {
    const sorted = [...(advertisers ?? [])].sort((a, b) => a.name.localeCompare(b.name));
    const terms = fold(filter.trim()).split(/\s+/).filter(Boolean);
    const matches = (a: AdvertiserRecord) => {
      if (a.key === pickedKey) return true; // keep the pick visible while filtering
      const text = fold([a.name, ...a.aliases].join(' '));
      return terms.every((t) => text.includes(t));
    };
    return {
      top: topKeys.map((k) => sorted.find((a) => a.key === k)).filter((a): a is AdvertiserRecord => !!a && matches(a)),
      rest: sorted.filter((a) => !topKeys.includes(a.key) && matches(a)),
    };
  }, [advertisers, filter, pickedKey, topKeys]);

  const picked = advertisers?.find((a) => a.key === pickedKey) ?? null;
  const pickedLogo = picked ? logoByKey.get(picked.key) : undefined;
  const optionLabel = (a: AdvertiserRecord) =>
    a.aliases.length > 0 ? `${a.name} — also ${a.aliases.slice(0, 3).join(', ')}${a.aliases.length > 3 ? '…' : ''}` : a.name;

  return (
    <Modal
      open
      onClose={onClose}
      eyebrow="Brand logos · Same as…"
      title={`Link “${row.name}” to a registry advertiser`}
      ariaLabel={`Link ${row.name} to a registry advertiser`}
      width={560}
      closeOnBackdrop={false}
      footer={
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button variant="primary" onClick={() => picked && onLink(picked)} disabled={!picked || saving}>
            {saving ? 'Linking…' : picked ? `Link to ${picked.name}` : 'Link'}
          </Button>
        </div>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14, fontSize: 13 }}>
        <div style={{ color: 'var(--fg-3)' }}>
          Creator reports show “{row.name}”. Pick the registry advertiser it is: the name is saved as one of that advertiser’s
          aliases, so its logo shows for this name too.
        </div>

        {row.match.state === 'ambiguous' && (
          <div style={{ ...bannerStyle('info'), display: 'block' }}>
            “{row.name}” matches several registry advertisers ({row.match.candidates.map((c) => c.name).join(', ')}), so no logo
            shows for it. Pick the right one. If more than one of them already lists the name as an alias, remove it from the wrong
            one in Sales → Rate Cards.
          </div>
        )}
        {suggestion && (
          <div style={mutedStyle}>
            Suggested: <strong style={{ color: 'var(--fg-2)' }}>{suggestion.name}</strong> ({suggestion.reason})
          </div>
        )}

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}>
            <Spinner label="Loading the advertiser registry…" />
          </div>
        ) : loadError ? (
          <div role="alert" style={bannerStyle('danger')}>
            <span>
              <strong>Couldn’t load the registry.</strong> <span style={{ color: 'var(--fg-3)' }}>{loadError}</span>
            </span>
            <Button size="sm" variant="ghost" onClick={onRetryLoad}>Retry</Button>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <label htmlFor={`${id}-pick`} style={{ ...labelStyle, marginBottom: 0 }}>Registry advertiser</label>
            <input
              style={fieldStyle}
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Filter by name or alias…"
              aria-label="Filter registry advertisers"
              disabled={saving}
            />
            <select
              id={`${id}-pick`}
              style={{ ...fieldStyle, cursor: 'pointer' }}
              value={pickedKey}
              disabled={saving}
              onChange={(e) => setPickedKey(e.target.value)}
            >
              <option value="">Choose an advertiser…</option>
              {top.length > 0 && (
                <optgroup label={topLabel}>
                  {top.map((a) => (
                    <option key={a.key} value={a.key}>{optionLabel(a)}</option>
                  ))}
                </optgroup>
              )}
              <optgroup label="All advertisers">
                {rest.map((a) => (
                  <option key={a.key} value={a.key}>{optionLabel(a)}</option>
                ))}
              </optgroup>
            </select>
            {top.length === 0 && rest.length === 0 && <div style={mutedStyle}>No registry advertiser matches “{filter.trim()}”.</div>}
          </div>
        )}

        {picked && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: 'var(--fg-3)' }}>
            {pickedLogo ? (
              <>
                <LogoPlate src={pickedLogo.url} background={pickedLogo.background} alt={`${picked.name} logo`} width={96} height={40} />
                <span>{picked.name}’s logo will show for “{row.name}”.</span>
              </>
            ) : (
              <span>{picked.name} has no logo yet — find or upload one after linking.</span>
            )}
          </div>
        )}

        {error && <div role="alert" style={{ ...bannerStyle('danger'), color: 'var(--danger)', fontWeight: 600 }}>{error}</div>}
      </div>
    </Modal>
  );
};

/** A listed row a typed brand name already is, and what to do with it instead. */
export interface ExistingBrand {
  message: string;
  actionLabel: string;
}

/**
 * Add a brand: one on no creator report and not in the registry yet — a new
 * advertiser before its first month lands. Asks for the name only; the
 * registry dialog that follows drafts the rest. A name the list already has
 * points at that row instead.
 */
export const AddBrandDialog: React.FC<{
  busy: boolean;
  error: string | null;
  /** The row this name already is, if any (checked as the name is typed). */
  existingFor: (name: string) => ExistingBrand | null;
  onContinue: (name: string) => void;
  onUseExisting: (name: string) => void;
  onClose: () => void;
}> = ({ busy, error, existingFor, onContinue, onUseExisting, onClose }) => {
  const id = useId();
  const [name, setName] = useState('');
  const existing = existingFor(name);
  const submit = () => {
    const typed = name.trim();
    if (!typed || busy) return;
    if (existing) onUseExisting(typed);
    else onContinue(typed);
  };
  return (
    <Modal
      open
      onClose={onClose}
      eyebrow="Brand logos"
      title="Add a brand"
      ariaLabel="Add a brand"
      width={480}
      closeOnBackdrop={false}
      footer={
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant="primary" onClick={submit} disabled={!name.trim() || busy}>
            {busy ? 'Preparing…' : existing ? existing.actionLabel : 'Continue'}
          </Button>
        </div>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
        style={{ display: 'flex', flexDirection: 'column', gap: 12, fontSize: 13 }}
      >
        <div style={{ color: 'var(--fg-3)' }}>
          For a brand that isn’t on a creator report or in the Sales registry yet — a new advertiser before its first month lands,
          say. It’s added to the registry first, then you upload or pick its logo.
        </div>
        <div>
          <label htmlFor={`${id}-name`} style={labelStyle}>Brand name</label>
          <input id={`${id}-name`} style={fieldStyle} value={name} maxLength={160} autoFocus disabled={busy} onChange={(e) => setName(e.target.value)} />
        </div>
        {existing && <div style={{ ...bannerStyle('info'), display: 'block' }}>{existing.message}</div>}
        {error && <div role="alert" style={{ ...bannerStyle('danger'), color: 'var(--danger)', fontWeight: 600 }}>{error}</div>}
      </form>
    </Modal>
  );
};
